
import React from 'react';
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, Upload, Download, FileX, Home } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface FileUploadSectionProps {
  isLoading: boolean;
  handleFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  downloadTemplate: () => void;
  clearData: () => void;
  priceDataLength: number;
  setShowDebug: (show: boolean) => void;
}

const FileUploadSection = ({
  isLoading,
  handleFileUpload,
  downloadTemplate,
  clearData,
  priceDataLength,
  setShowDebug
}: FileUploadSectionProps) => {
  // Create a reference to the file input element
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <Button 
          variant="outline" 
          className="relative"
          onClick={() => {
            // Reset file input value before clicking to ensure onChange fires even if same file is selected
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
              fileInputRef.current.click();
            }
          }}
          disabled={isLoading}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={isLoading}
          />
          <FileSpreadsheet className="mr-2 h-4 w-4" />
          <span>{isLoading ? "Loading..." : "Upload Excel File"}</span>
          <Upload className="ml-2 h-3 w-3" />
        </Button>
        
        <Button variant="secondary" onClick={downloadTemplate}>
          <Download className="mr-2 h-4 w-4" />
          Download Template
        </Button>
        
        {priceDataLength > 0 && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  variant="outline" 
                  onClick={clearData}
                  className="text-red-500 border-red-500 hover:bg-red-50"
                >
                  <FileX className="mr-2 h-4 w-4" />
                  Clear Data
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Remove all loaded pricing data from memory and storage</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        
        <Link to="/" className="ml-auto">
          <Button variant="outline">
            <Home className="mr-2 h-4 w-4" />
            Go To Home
          </Button>
        </Link>
        
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => setShowDebug(true)}
          className="text-xs"
        >
          Debug Info
        </Button>
      </div>
      
      <div className="text-sm text-muted-foreground">
        <p className="mb-2">Upload an Excel file with exactly these column names:</p>
        <ul className="list-disc list-inside pl-4 space-y-1">
          <li>Product Name: The full product name</li>
          <li>Product Description: The product identifier (e.g., VPX-TC1-PRO)</li>
          <li>MSRP: The manufacturer's suggested retail price</li>
        </ul>
      </div>
    </div>
  );
};

export default FileUploadSection;
